import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_LEVELS } from '@/domains/pages/ai-proficiency/constants';
import type { LevelBenchmarkShare } from '@/domains/pages/ai-proficiency/fixtures/benchmarks';
import { isLevelShareLowerBetter } from '@/domains/pages/ai-proficiency/utils/ladder';
import type { SegmentSummary } from '@/domains/pages/ai-proficiency/utils/map-segment-aggregates';

export type SegmentScoreStats = {
  median: number;
  bestScore: number;
  bestName: string;
};

export type { WorstBestEnvelopePoint } from '@/domains/pages/shared/segment-envelope';
export { computeWorstBestEnvelope, inferWorstBestEnvelope } from '@/domains/pages/shared/segment-envelope';

export function computeMedianScore(scores: number[]): number {
  if (scores.length === 0) return 0;
  const sorted = [...scores].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

export function computeSegmentScoreStats(segments: SegmentSummary[]): SegmentScoreStats {
  const scored = segments.filter((segment) => segment.score != null);
  const scores = scored.map((segment) => segment.score!);
  const median = computeMedianScore(scores);

  let bestScore = 0;
  let bestName = '';
  for (const segment of scored) {
    if (segment.score != null && segment.score >= bestScore) {
      bestScore = segment.score;
      bestName = segment.name;
    }
  }

  return { median, bestScore, bestName };
}

export function computeSegmentRank(segments: SegmentSummary[], selectedName: string): number {
  const ranked = [...segments]
    .filter((segment) => segment.score != null)
    .sort((left, right) => right.score! - left.score!);

  const index = ranked.findIndex((segment) => segment.name === selectedName);
  return index >= 0 ? index + 1 : 0;
}

export function computeRankAtBucket(
  segmentScoresByName: Record<string, Array<number | null>>,
  selectedName: string,
  bucketIndex: number,
): number {
  const selectedScore = segmentScoresByName[selectedName]?.[bucketIndex] ?? null;
  if (selectedScore == null) return 0;

  const scores = Object.entries(segmentScoresByName)
    .map(([name, series]) => ({ name, score: series[bucketIndex] ?? null }))
    .filter((entry): entry is { name: string; score: number } => entry.score != null)
    .sort((a, b) => b.score - a.score);

  const index = scores.findIndex((entry) => entry.name === selectedName);
  return index >= 0 ? index + 1 : 0;
}

/** Expert/Practitioner: highest share wins; Beginner/Novice: lowest share wins. */
export function computeBestLevelBenchmarks(
  segmentLevelShares: Record<string, Record<AifiLevel, number>>,
): Record<AifiLevel, LevelBenchmarkShare & { bestSegmentName: string }> {
  const result = {} as Record<AifiLevel, LevelBenchmarkShare & { bestSegmentName: string }>;

  for (const level of AIFI_LEVELS) {
    const isLowerBetter = isLevelShareLowerBetter(level);
    let bestShare = isLowerBetter ? Infinity : -Infinity;
    let bestSegmentName = '';

    for (const [segmentName, shares] of Object.entries(segmentLevelShares)) {
      const share = shares[level] ?? 0;
      const isBetter = isLowerBetter ? share < bestShare : share > bestShare;
      if (isBetter) {
        bestShare = share;
        bestSegmentName = segmentName;
      }
    }

    if (bestShare === Infinity || bestShare === -Infinity) {
      bestShare = 0;
    }

    result[level] = {
      topQuartileShare: bestShare,
      bestSegmentName,
    };
  }

  return result;
}
